// commit-owner-git-ops.ts
//
// Thin git primitives used by commit-owner.ts, split out to keep both files
// under the 200-line budget. `GitRunner` is INJECTED so tests can record
// argv (test 33: assert no `push` call ever happens) while still exercising
// a real git repo end-to-end.
export interface GitRunResult {
  status: number | null;
  stdout: string;
  stderr: string;
}

/** Per-call extras. `env` is merged over the process env (N-04: `GIT_INDEX_FILE` for a temporary index). Optional so every argv-recording fake `(args, cwd) => …` stays assignable. */
export interface GitRunOptions {
  env?: Record<string, string>;
}

export type GitRunner = (
  args: string[],
  cwd: string,
  options?: GitRunOptions,
) => Promise<GitRunResult>;

async function run(gitRunner: GitRunner, args: string[], cwd: string): Promise<string> {
  const res = await gitRunner(args, cwd);
  if (res.status !== 0) {
    throw new Error(
      `commit-owner: git ${args.join(" ")} thất bại (status ${res.status}): ${res.stderr}`,
    );
  }
  return res.stdout.trim();
}

export async function revParse(gitRunner: GitRunner, cwd: string, ref: string): Promise<string> {
  return run(gitRunner, ["rev-parse", ref], cwd);
}

/** `HEAD` may not exist yet (bootstrap commit into a brand-new repo) — never throws. */
export async function tryRevParseHead(
  gitRunner: GitRunner,
  cwd: string,
): Promise<string | undefined> {
  const res = await gitRunner(["rev-parse", "HEAD"], cwd);
  return res.status === 0 ? res.stdout.trim() : undefined;
}

/**
 * Clears the index back to HEAD so a later `stagePaths` commits EXACTLY its
 * manifest and nothing else.
 *
 * Audit 2026-09-14 finding 3 (link 2): `git add` ADDS to the index, it does not
 * replace it. A verification recipe running as the worker can `git add` a file
 * of its own — the audit's mission staged `.avatar/verification-profile.json`,
 * the rulebook the verifier judges it by — and commit-owner then swept it into
 * the commit despite it being absent from `manifestPaths`.
 *
 * Note `git reset -q --` with no pathspec is a NO-OP; the pathspec `.` is what
 * actually unstages — and `.` is resolved against `cwd`, so this clears the
 * WHOLE index only because every call site passes the repo root (`repoDir`);
 * called from a subdirectory it would reset that subtree alone (code review
 * 260915 L1). Caller must skip this entirely when the repo has no HEAD yet
 * (the bootstrap commit into a brand-new repo): there is nothing to reset
 * against and git errors out.
 *
 * Worktree contents are untouched (mixed reset) — only the index is cleared, so
 * the manifest files are still there to be staged a moment later.
 */
export async function resetIndex(gitRunner: GitRunner, cwd: string): Promise<void> {
  await run(gitRunner, ["reset", "-q", "--", "."], cwd);
}

/**
 * The pathspecs that match at least one stageable file: tracked (a deleted
 * tracked file still counts — its deletion is what gets staged) or untracked
 * and not ignored.
 *
 * X3 (close-out 2026-09-30): `git add -- <p>` exits 128 when `p` matches no
 * such file (`src/**` in a repo with no `src/`, a glob matching only ignored
 * files) and exits 1 on an explicitly named ignored file. Either one threw out
 * of commit-owner and crashed `run` again on every rerun of a ticket whose
 * allowed_paths had no file yet. Probed one pathspec at a time — a ticket
 * declares a handful — so a matching glob is still handed to `git add` as the
 * glob, never expanded into argv.
 *
 * Skipped outright (review 260930):
 *  - git magic (`:!x`, `:(exclude)x`, `:/x`): the verifier reads allowed_paths
 *    as mini-globs, where these are literal text matching no file — and an
 *    exclude left alone makes `git add` stage EVERYTHING but it (M1);
 *  - a pathspec `ls-files` rejects (`../x` outside the repo, exit 128): it can
 *    never be staged either, and throwing here crash-looped `run` (L1).
 */
async function stageablePathspecs(
  gitRunner: GitRunner,
  cwd: string,
  paths: string[],
): Promise<{ kept: string[]; skipped: string[] }> {
  const kept: string[] = [];
  const skipped: string[] = [];
  for (const pathspec of paths) {
    if (pathspec.startsWith(":")) {
      skipped.push(pathspec);
      continue;
    }
    const res = await gitRunner(
      ["ls-files", "--cached", "--others", "--exclude-standard", "--", pathspec],
      cwd,
    );
    if (res.status === 0 && res.stdout.trim().length > 0) kept.push(pathspec);
    else skipped.push(pathspec);
  }
  return { kept, skipped };
}

/**
 * Stages the manifest; pathspecs with nothing to stage are skipped (see
 * stageablePathspecs) and RETURNED, so the caller can tell the operator — a
 * `done` ticket whose scope matched only ignored files used to end `no_change`
 * without a trace (review 260930 L5).
 */
export async function stagePaths(
  gitRunner: GitRunner,
  cwd: string,
  paths: string[],
): Promise<string[]> {
  const { kept, skipped } = await stageablePathspecs(gitRunner, cwd, paths);
  if (kept.length > 0) await run(gitRunner, ["add", "--", ...kept], cwd);
  return skipped;
}

/** True if the index has staged changes relative to HEAD (nothing to commit otherwise). */
export async function hasStagedChanges(gitRunner: GitRunner, cwd: string): Promise<boolean> {
  const res = await gitRunner(["diff", "--cached", "--quiet"], cwd);
  // `git diff --cached --quiet` exits 1 when there ARE differences, 0 when clean.
  return res.status === 1;
}

export async function commitStaged(
  gitRunner: GitRunner,
  cwd: string,
  message: string,
): Promise<string> {
  await run(gitRunner, ["commit", "-q", "-m", message], cwd);
  return revParse(gitRunner, cwd, "HEAD");
}

export function buildCommitMessage(input: {
  type: string;
  scope: string;
  subject: string;
  ticketRef: string;
  missionId: string;
  runId: string;
}): string {
  const subjectLine = `${input.type}(${input.scope}): ${input.subject} [${input.ticketRef}]`;
  return [
    subjectLine,
    "",
    `Autopilot-Mission: ${input.missionId}`,
    `Autopilot-Run: ${input.runId}`,
  ].join("\n");
}
